
// Deployment trigger: 2026-04-24
import React, { useState } from 'react';
import { Brain, CheckCircle2, XCircle } from 'lucide-react';
import { Question } from '../../types';

interface TriviaProps {
  questions: Question[];
  onWin: () => void;
  onLoss: () => void;
  isLightMode?: boolean;
}

const Trivia: React.FC<TriviaProps> = ({ questions, onWin, onLoss, isLightMode }) => {
  const currentIdx = 0; // Simplified for MVP to show first question
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);

  const question = questions[currentIdx] || questions[0];

  const handleSelect = (idx: number) => {
    if (isAnswered) return;
    setSelectedIdx(idx);
    setIsAnswered(true);
    
    const isCorrect = idx === question.correctIndex;
    setTimeout(() => {
      if (isCorrect) onWin();
      else onLoss();
    }, 1500);
  };

  return (
    <div className="animate-in fade-in duration-500">
      <div className="text-center mb-8">
        <Brain className={`w-12 h-12 ${isLightMode ? 'text-indigo-600' : 'text-indigo-400'} mx-auto mb-4`} />
        <h3 className={`${isLightMode ? 'text-slate-800' : 'text-white'} font-black text-xl uppercase tracking-widest italic leading-none`}>Quick Quiz</h3>
        <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest mt-2">Answer correctly to win</p>
      </div>

      <div className={`rounded-3xl p-6 border mb-8 ${isLightMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-900/50 border-white/5'}`}>
        <p className={`${isLightMode ? 'text-slate-800' : 'text-white'} font-bold text-sm leading-relaxed mb-6`}>
          {question.text}
        </p>
        <div className="space-y-3">
          {question.options.map((opt, idx) => {
            let statusClass = isLightMode ? "border-slate-200 bg-white text-slate-600" : "border-white/5 bg-slate-800/50 text-slate-300";
            if (isAnswered) {
              if (idx === question.correctIndex) statusClass = "border-emerald-500 bg-emerald-500/10 text-emerald-600 font-black";
              else if (idx === selectedIdx) statusClass = "border-red-500 bg-red-500/10 text-red-600 font-black";
              else statusClass = "opacity-30 border-slate-200 bg-white text-slate-600";
            } else {
              statusClass = isLightMode 
                ? "border-slate-200 bg-white text-slate-600 hover:border-indigo-500 hover:text-indigo-600" 
                : "border-white/5 bg-slate-800/50 text-slate-300 hover:border-indigo-500 hover:text-white";
            }

            return (
              <button
                key={idx}
                onClick={() => handleSelect(idx)}
                disabled={isAnswered}
                className={`w-full p-4 rounded-2xl border transition-all text-xs font-bold text-left flex items-center justify-between ${statusClass}`}
              >
                {opt}
                {isAnswered && idx === question.correctIndex && <CheckCircle2 className="w-4 h-4" />}
                {isAnswered && idx === selectedIdx && idx !== question.correctIndex && <XCircle className="w-4 h-4" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Trivia;
